import { useQuery } from '@apollo/client'
import Button from 'components/Button'
import { CardDimensions } from 'components/CourseCard/CourseCard'
import { useLocation } from 'context/Location/LocationProvider'
import { AnimatePresence, motion } from 'framer-motion'
import { GET_USER } from 'gql/users'
import { getCoursePrice } from 'lib/currency'
import { formatPrice } from 'lib/utils'
import { useSession } from 'next-auth/client'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Select from 'react-select'
import { Course } from 'types'
import { CourseIntake, StartType } from 'types/course'
import { User } from 'types/user'
import CalendarIcon from '../../svg/calendar.svg'
import ClockIcon from '../../svg/clock.svg'
import DocumentIcon from '../../svg/document.svg'

type CourseDetailsCardProps = {
  course: Course
  options: CourseIntakeOption[]
  selectedOption: CourseIntakeOption
  setSelectedOption: (option: CourseIntakeOption) => void
  variant?: 'default' | 'sticky'
  isVisible?: boolean
}

export type CourseIntakeOption = {
  value: string
  label: string
  intake: CourseIntake
}

const CourseDetailsCard = ({
  course,
  options,
  selectedOption,
  setSelectedOption,
  variant = 'default',
  isVisible = true,
}: CourseDetailsCardProps) => {
  const { t } = useTranslation()
  const {
    title,
    timeframeWeeks,
    hoursPerWeek,
    isComingSoon,
    imageUrl,
    isFree,
  } = course

  const [session] = useSession()
  const { country } = useLocation()

  const handleChangeIntake = (value: string) => {
    const intake = options.find((option) => option.value === value)
    if (intake) {
      setSelectedOption(intake)
    }
  }

  const { data: userData } = useQuery<{ me: User }>(GET_USER, {
    skip: !session?.user,
  })
  const user = session?.user && userData?.me

  // Calculate the course price based on the stored country code (either from cookies or user)
  const coursePrice = getCoursePrice(course, user, country)
  const enrolUrl = selectedOption && `/enrol/${selectedOption.value}`
  const showSelector = options && course.startType !== StartType.ROLLING

  const isSticky = variant === 'sticky'

  const cardContent = (
    <div className='flex flex-col gap-4 flex-1'>
      <div className='text-xl font-semibold'>
        {isFree ? 'Free' : formatPrice(coursePrice[0], coursePrice[1], true)}
      </div>
      <div className='flex'>
        {timeframeWeeks && (
          <div className='flex px-0.5'>
            <ClockIcon className='text-purple-heart' />
            <div className='text-sm font-medium px-2 pt-0.5'>
              {timeframeWeeks} {t('weeks')}
            </div>
          </div>
        )}
        {hoursPerWeek && (
          <div className='flex px-0.5'>
            <DocumentIcon className='text-purple-heart' />
            <div className='text-sm font-medium px-2 pt-0.5'>
              {hoursPerWeek} hours/week
            </div>
          </div>
        )}
      </div>
      <div className='flex justify-between items-center'>
        {showSelector && (
          <Select
            options={options}
            className='w-full'
            value={selectedOption}
            onChange={(event) => event && handleChangeIntake(event.value)}
          />
        )}
        {selectedOption && !showSelector && (
          <div className='flex px-0.5'>
            <CalendarIcon className='text-purple-heart' />
            <div className='text-sm font-medium px-2 pt-0.5'>
              {selectedOption.label}
            </div>
          </div>
        )}
      </div>
      <Button
        className='flex-grow'
        href={enrolUrl}
        target='_self'
        disabled={!enrolUrl}
      >
        Enrol now
      </Button>
    </div>
  )

  if (isSticky) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{
              y: 230,
              // opacity: 0
            }}
            animate={{
              y: 0,
              // opacity: 1
            }}
            exit={{
              y: 230,
              // opacity: 0
            }}
            transition={{
              y: {
                type: 'spring',
                damping: 30,
                stiffness: 200,
              },
              opacity: {
                // duration: 1,
              },
            }}
            className='fixed bottom-0 left-0 md:left-80 right-0 backdrop-filter backdrop-blur-lg bg-white bg-opacity-70 border-t border-mischka z-50'
          >
            <div className='container mx-auto max-w-7xl'>
              <div className='w-full lg:w-80 ml-auto'>
                <div className='flex-1 p-6 flex flex-col justify-between'>
                  {cardContent}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <div className='flex flex-col rounded-lg h-auto border-mischka border-1 bg-white'>
      {!isSticky && (
        <div
          className='relative flex-shrink-0 text-0 rounded-t-lg overflow-hidden'
          style={{
            aspectRatio: `${CardDimensions.Width} / ${CardDimensions.Height}`,
          }}
        >
          {isComingSoon && (
            <div className='absolute top-4 right-4 flex gap-2'>
              <div className='bg-white items-center px-4 py-2 rounded-full z-10'>
                <div className='leading-5 text-xs font-medium'>
                  {t('Coming Soon')}
                </div>
              </div>
            </div>
          )}
          {imageUrl && (
            <Image
              className='w-full object-cover'
              src={imageUrl}
              alt={title}
              width={CardDimensions.Width}
              height={CardDimensions.Height}
              priority
            />
          )}
        </div>
      )}
      <div className='flex-1 p-6 flex flex-col justify-between'>
        {cardContent}
      </div>
    </div>
  )
}

export default CourseDetailsCard
